import { useEffect, useState } from 'react';

/**
 * Subscribe to a CSS media query and re-render when it flips.
 *
 * Mirrors the `window.matchMedia` pattern used by the theme system in
 * `lib/theme.tsx`; SSR/non-browser environments fall back to `false`.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    // Sync once in case the query changed between render and effect.
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/**
 * True below Tailwind's `md` breakpoint (768px), i.e. phone-sized viewports.
 * The dashboard sidebar is `md:flex`, so this matches "no desktop sidebar".
 */
export function useIsMobile(): boolean {
  return useMediaQuery('(max-width: 767px)');
}
